import React from "react";
import { Typography, Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from "@mui/material";
import { StepContainer } from "./StepContainer";
import { GroupStatistics, PreviousExport } from "../types";

interface ConfirmExportStepProps {
  handleBack: () => void;
  handleSubmit: () => void;
  isLoading: boolean;
  isSuperAdmin: boolean;
  hasPreviousExports: boolean;
  groupStatistics: GroupStatistics;
  previousExports: PreviousExport[];
  onDownloadCsv: (runId: string) => Promise<void>;
}

export const ConfirmExportStep: React.FC<ConfirmExportStepProps> = ({
  handleBack,
  handleSubmit,
  isLoading,
  groupStatistics,
  previousExports,
  onDownloadCsv
}) => {
  const leftPanel = (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
      <Typography
        variant="body1"
        sx={{
          fontSize: '16px',
          color: '#757575',
          lineHeight: 1.6,
          mb: 3
        }}
      >
        Before you export, please note that {groupStatistics.singleResidents} single residents will not be exported.
      </Typography>

      <Typography
        variant="body1"
        sx={{
          fontSize: '16px',
          color: '#424242',
          mb: 3
        }}
      >
        Would you still like to export to Entrata?
      </Typography>

      <Box display="flex" gap={2}>
        <Button
          variant="outlined"
          onClick={handleBack}
          sx={{
            borderColor: '#1e3a5f',
            color: '#1e3a5f',
            '&:hover': {
              borderColor: '#1e3a5f',
              backgroundColor: 'rgba(30, 58, 95, 0.04)'
            },
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '14px',
            padding: '10px 24px',
            borderRadius: '8px',
            minWidth: '140px'
          }}
        >
          No, Go Back
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isLoading}
          sx={{
            bgcolor: '#2167e2',
            '&:hover': {
              bgcolor: '#1a52b3'
            },
            '&:disabled': {
              bgcolor: '#ccc',
              color: '#666'
            },
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '14px',
            padding: '10px 24px',
            borderRadius: '8px',
            boxShadow: 'none',
            minWidth: '140px'
          }}
        >
          {isLoading ? "Exporting..." : "Yes, Export"}
        </Button>
      </Box>
    </Box>
  );

  const rightPanel = previousExports.length > 0 ? (
    <Box sx={{ width: '100%' }}>
      <Typography variant="subtitle1" fontWeight="500" mb={2} sx={{ fontSize: '16px' }}>
        Previous Exports
      </Typography>
      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E0E0E0', borderRadius: 1 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#F5F5F5' }}>
              <TableCell sx={{ fontWeight: 600, fontSize: '14px', color: '#424242' }}>Export Date</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '14px', color: '#424242' }}>Successful Groups</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '14px', color: '#424242' }}>Unsuccessful Groups</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '14px', color: '#424242' }}>Single Residents</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '14px', color: '#424242' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '14px', color: '#424242' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {previousExports.map((exportItem) => (
              <TableRow key={exportItem.id} sx={{ '&:hover': { bgcolor: '#F9F9F9' } }}>
                <TableCell sx={{ fontSize: '14px' }}>{exportItem.exportDate}</TableCell>
                <TableCell sx={{ fontSize: '14px' }}>{exportItem.successfulGroups}</TableCell>
                <TableCell sx={{ fontSize: '14px' }}>{exportItem.unsuccessfulGroups}</TableCell>
                <TableCell sx={{ fontSize: '14px' }}>{exportItem.singleResidents}</TableCell>
                <TableCell>
                  <Box sx={{
                    display: 'inline-block',
                    bgcolor: exportItem.status === 'DONE' ? '#D1FAE5' : '#FEF3C7',
                    color: exportItem.status === 'DONE' ? '#065F46' : '#92400E',
                    px: 2,
                    py: 0.5,
                    borderRadius: '16px',
                    fontSize: '12px',
                    fontWeight: 500
                  }}>
                    {exportItem.status}
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography
                    onClick={() => onDownloadCsv(exportItem.runId)}
                    sx={{
                      color: '#2167e2',
                      fontSize: '14px',
                      fontWeight: 500,
                      cursor: 'pointer',
                      textDecoration: 'none',
                      '&:hover': {
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    Download CSV
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  ) : null;

  return (
    <StepContainer leftPanel={leftPanel} rightPanel={rightPanel} />
  );
};
